import { Edit, Gavel } from "@mui/icons-material";
import { Box, FormControl, Grid, IconButton, Paper, TextField, Tooltip } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { IFilteringViewModel } from "../../_common/interface/table";
import { IUserFineList } from "../../_common/interface/userFine/iUserFineList";
import { PrimaryButton } from "../../_components/button/primary.button";
import StyledGrid from "../../_components/grid/styledGrid";
import { PageHeader } from "../../_components/header/page.header";
import { CommonTable } from "../../_components/table/common.table";
import { getDate } from "../../_helpers/dateTime.helper";
import { getMoneyFormat } from "../../_helpers/format.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { usersService } from "../../_services/users.service";
import { useContainerStyles } from "../../_styles/container.styles";
import { UserFineManageDrawer } from "../userFine/userFine-manageDrawer";
import { AccountManageDrawer } from "./account-manageDrawer";

export function AccountIndex() {

    const [data, setData] = useState({ items: [], count: 0 });
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [userFineSelectedId, setUserFineSelectedId] = useState<string | null>(null);
    const [userFineId, setUserFineId] = useState<number | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [filter, setFilter] = useState<IFilteringViewModel | null>(null);

    const [search, setSearch] = useState<any>({
        orderBy: "id",
        name: '',
        email: '',
    });

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'actions',
                header: 'Actions',
                Cell: ({ cell, table, row }) => (

                    <>
                        <Tooltip title="Edit">
                            <IconButton onClick={e => { setSelectedId(row.original.id); }}>
                                <Edit color="primary" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Add Fine">
                            <IconButton onClick={e => { setUserFineSelectedId(row.original.id); }}>
                                <Gavel color="warning" />
                            </IconButton>
                        </Tooltip>
                    </>

                ),
                size: 5
            },
            {
                accessorKey: 'name',
                header: 'Name',
            },
            {
                accessorKey: 'email',
                header: 'Email',
            },
            {
                accessorKey: 'fines',
                header: 'User Fines',
                Cell: ({ cell }) => {
                    const fines = cell.getValue<IUserFineList[]>() || [];

                    return (
                        <div>
                            {fines.length === 0 ? (
                                ""
                            ) : (
                                fines.map((item: any) => (
                                    <div key={item.id}>
                                        <Box display="flex" gap={1} alignItems="center">
                                            {item.name} {item.isChargeable ? !item.isPaid ? getMoneyFormat(item.price) : "" : ""} <span style={{ color: "red" }}>{item.isBannedIndefinitely ? "Indefinitely Banned" : item.isPaid ? "Paid" : ""}</span>
                                            <Tooltip title="Manage">
                                                <IconButton onClick={() => setUserFineId(item.id)}>
                                                    <Edit fontSize="small" color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </div>
                                ))
                            )}
                        </div>
                    );
                },
                size: 300
            },
            {
                accessorKey: 'status',
                header: 'Status',
            },
            {
                accessorKey: 'dateOfBirth',
                header: 'Date Of Birth',
                accessorFn: (rowData: any) => <>{rowData.dateOfBirth ? getDate(rowData.dateOfBirth) : "-"}</>,
            },
        ],
        [],
    );

    useEffect(() => {
        if (filter)
            fetchData();
    }, [filter]);

    function fetchData() {
        setLoading(true)
        return usersService.getAllAccounts({ ...filter, ...search })
            .then(
                data => {
                    setData(data);
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    function handleChange(event: any) {
        setSearch((__filter: any) => { return { ...__filter, [event.target.name]: event.target.value } });
    };


    return (
        <>
            <div style={useContainerStyles.root}>

                <PageHeader
                    pageName="Accounts"
                    buttonName="Account"
                    onClick={() => setSelectedId("")}
                    actionType="create"
                />

                <Paper>

                    <ValidatorForm onSubmit={fetchData}>
                        <StyledGrid container spacing={2}>

                            <Grid item lg={3} md={6} xs={12}>

                                <FormControl fullWidth>
                                    <TextField label="Name" name="name" value={search.name} onChange={handleChange} placeholder="Name" variant="outlined" />
                                </FormControl>
                            </Grid>

                            <Grid item lg={3} md={6} xs={12}>
                                <FormControl fullWidth>
                                    <TextField label="Email" name="email" value={search.email} onChange={handleChange} placeholder="Email" variant="outlined" />
                                </FormControl>
                            </Grid>
                            <Grid item lg={2} md={6} xs={12}>
                                <PrimaryButton
                                    fullHeight
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{"Search"}
                                </PrimaryButton>
                            </Grid>


                        </StyledGrid>
                    </ValidatorForm>

                    <CommonTable
                        columns={columns}
                        data={data}
                        filter={filter}
                        setFilter={setFilter}
                        isLoading={isLoading}
                    />

                </Paper>
            </div>

            <AccountManageDrawer
                isOpen={selectedId != null}
                itemId={selectedId}
                onSave={() => { setSelectedId(null); fetchData(); }}
                onClose={() => { setSelectedId(null); fetchData(); }}

            />

            <UserFineManageDrawer
                isOpen={userFineSelectedId != null || userFineId != null}
                userId={userFineSelectedId}
                userFineId={userFineId}
                onSave={() => { setUserFineSelectedId(null); setUserFineId(null); fetchData(); }}
                onClose={() => { setUserFineSelectedId(null); setUserFineId(null); fetchData(); }}
            />
        </>
    );

}
